import type { ComponentType } from 'react';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import type { LWApiPublicDomainDetailsRawI } from '@liquidweb-dev/club-sauce/public/domain/index.raw';
import CheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/Check';
import CircleDashedIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleDashed';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';

import getExpirationStatus from 'modules/ssl/getExpirationStatus';

export { getExpirationStatus };
/**
 * Domain status types
 */
export type DomainStatus = 'active' | 'expired' | 'external' | 'registering';

/**
 * Get the overall status of a domain
 * @param domain - The domain item
 * @returns The domain status: 'active', 'expired' or 'external'
 */
export const getDomainStatus = (
	domain: LWApiPublicDomainDetailsRawI,
): DomainStatus => {
	// If domain is not registered with us, it's external
	if (!domain.dreg) {
		return 'external';
	}

	const expirationStatus = getExpirationStatus({
		expiration: domain.dreg.expiration,
		autorenew: fromApiBoolean(domain.dreg.autorenew),
	});

	if (expirationStatus === 'expired') {
		return 'expired';
	}

	return 'active';
};

export const getDomainStatusProps = (
	status: DomainStatus,
): {
	Icon: ComponentType<LucideSvgProps>;
	color: 'default' | 'success' | 'warning' | 'highlight' | 'danger';
	primaryInfo: string;
} => {
	switch (status) {
		case 'active': {
			return { Icon: CheckIcon, color: 'success', primaryInfo: 'Registered' };
		}

		case 'expired': {
			return {
				Icon: XIcon,
				color: 'default',
				primaryInfo: 'Expired',
			};
		}

		case 'external': {
			return {
				Icon: CircleDashedIcon,
				color: 'default',
				primaryInfo: 'External',
			};
		}

		default: {
			return {
				Icon: CircleDashedIcon,
				color: 'default',
				primaryInfo: 'External',
			};
		}
	}
};

/**
 * Get sorting priority for domain lists
 * Priority: 1 = active, 2 = external, 3 = inactive/expired
 * @param domain - The domain item
 * @returns Priority number for sorting (lower = higher priority)
 */
export const getDomainSortPriority = (
	domain: LWApiPublicDomainDetailsRawI,
): number => {
	const domainStatus = getDomainStatus(domain);
	if (domainStatus === 'active') return 1;
	if (domainStatus === 'external') return 2;
	if (domainStatus === 'expired') return 3;
	return 4; // fallback
};
